import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserService } from '@shared/services/user.service';
import { IItemBase, IResponsibleUser, IUser, scdUserConverter } from '@shared/interfaces';
import { PageEvent } from '@angular/material/paginator';
import { IPaginatedResult } from '@shared/interfaces/mailing/IPaginatedResult';
import { IQueryUser } from '@shared/interfaces/queries/IQueryUser';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { SCDService } from '@shared/services';
import { flatten } from 'lodash-es';
import { disableBackgroundScrolling, enableBackgroundScrolling } from '@shared/helpers/helpers';

@Component({
    selector: 'reporting-tool-user-selection',
    templateUrl: './reporting-tool-user-selection.component.html',
    styleUrls: ['./reporting-tool-user-selection.component.scss'],
    standalone: false
})
export class ReportingToolUserSelectionComponent implements OnInit, OnDestroy {
    @ViewChild('itemContainer') itemContainer: ElementRef;
    pagedData: IPaginatedResult<IUser>;
    value: IUser[] = [];
    event: PageEvent;
    loading: boolean;
    keyUp = new Subject<KeyboardEvent>();
    subscription: Subscription;
    isLoadedExternal: boolean;
    isLoadingExternal: boolean;

    query: {
        firstName: string;
        lastName: string;
        eMail: string;
        gid: string;
    } = { firstName: '', lastName: '', eMail: '', gid: '' };

    constructor(
        public dialogRef: MatDialogRef<ReportingToolUserSelectionComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            excluded: IUser[] | IResponsibleUser[];
            selected: IUser[];
            fullData: boolean;
            multiple: boolean;
            disableExternal?: boolean;
        },
        private userService: UserService,
        private scdService: SCDService
    ) {}

    get paginatedData(): IUser[] {
        return this.pagedData?.items ?? [];
    }

    get isQuerying(): boolean {
        return this.query.gid?.length === 8 || !!this.query.eMail || !!this.query.firstName || !!this.query.lastName;
    }

    ngOnDestroy() {
        enableBackgroundScrolling();
        this.subscription.unsubscribe();
    }

    changeSelected(element: IUser) {
        if (!this.data.multiple) {
            this.pagedData.items.forEach(e => (e.selected = false));
        }
        element.selected = !element.selected;
    }

    async ngOnInit() {
        disableBackgroundScrolling();
        this.subscription = this.keyUp.pipe(debounceTime(500), distinctUntilChanged()).subscribe(_ => this.loadData());

        return this.loadData();
    }

    closeDialog() {
        const values = this.pagedData.items
            .filter(e => e.selected && !this.data?.selected?.some(a => a.id === e.id))
            .map(e =>
                e.isExternal
                    ? e
                    : this.data?.fullData
                      ? e
                      : {
                            id: e.id,
                            fullName: e.userData.fullName,
                            eMail: e.userData.eMail
                        }
            );
        this.dialogRef.close(this.data.multiple ? values : values[0]);
    }

    async loadData(event?: PageEvent) {
        if (
            !this.pagedData ||
            !event ||
            (this.pagedData &&
                event.pageIndex > this.pagedData.page &&
                (event.pageIndex + 1) * event.pageSize > this.pagedData.items.length)
        ) {
            const query: IQueryUser = {
                firstName: this.query.firstName ?? null,
                lastName: this.query.lastName ?? null,
                eMail: this.query.eMail ?? null,
                gid: this.query.gid ?? null,
                exclude: this.data.excluded?.length ? this.data.excluded?.map(e => e.id) : null,
                pagination: {
                    page: event?.pageIndex ?? 0,
                    pageSize: 20
                }
            };

            const paginatedResult = await this.userService.getAllUsersFlat(query);
            const data = paginatedResult.items;
            if (this.data?.excluded?.length) {
                data.forEach(e => ((e as IItemBase).disabled = this.data?.excluded.some(a => a.id === e.id)));
            }

            if (this.data?.selected?.length) {
                data.forEach(e => ((e as IItemBase).selected = this.data?.selected.some(a => a.id === e.id)));
            }

            if (event) {
                this.pagedData = {
                    ...paginatedResult,
                    items: [...(this.pagedData?.items ?? []), ...paginatedResult.items]
                };
            } else {
                this.pagedData = paginatedResult;
                setTimeout(() => this.setScrollListener(), 50);
            }
        }

        this.event = event ?? ({ pageIndex: 0, pageSize: 20 } as PageEvent);

        if (this.isQuerying && !this.pagedData.items.length && !this.data?.disableExternal) {
            this.loadExternal();
        } else {
            this.isLoadedExternal = false;
        }
    }

    private setScrollListener() {
        const itemContainer = this.itemContainer?.nativeElement;
        if (itemContainer) {
            itemContainer.onscroll = async () => {
                if (this.pagedData.hasMore) {
                    const pos = (itemContainer.scrollTop || itemContainer.scrollTop) + itemContainer.offsetHeight;
                    const max = itemContainer.scrollHeight;

                    if (pos >= max - 1 && !this.loading) {
                        this.loading = true;
                        this.event.pageIndex++;
                        await this.loadData(this.event);
                        this.loading = false;
                    }
                }
            };
        }
    }

    public async loadExternal(isLoadedMore = false) {
        this.isLoadedExternal = true;
        this.isLoadingExternal = true;

        const items = await this.scdService.searchUserInSCD(
            this.query.firstName,
            this.query.lastName,
            this.query.eMail,
            this.query.gid
        );
        let result = scdUserConverter.toApiMulti(flatten(items.records)).map(e => ({
            userData: e,
            isExternal: true,
            id: e.id
        }));

        if (isLoadedMore) {
            if (this.pagedData?.items) {
                result = result.filter(e => !this.pagedData.items.some(i => (i.id || i.userData.id) === e.userData.id));
            }

            this.pagedData = {
                ...this.pagedData,
                items: [...(this.pagedData?.items ?? []), ...(result as any[])]
            };
        } else {
            this.pagedData = { items: result };
        }

        this.event = { pageIndex: 0, pageSize: 20 } as PageEvent;
        this.isLoadingExternal = false;
    }
}
